//------------------------------------------------------ funciones
import {
  subscribeTable,
  createRow,
  updateRow,
  deleteRow,
} from "../supabaseRealtime";
import { cacheStore } from "../cache";
import { etiquetaDeFicha } from "../../shared/empresa";
import {
  origenDeEstado,
  textoDeEstadoTractor,
  tractorEstaActivo,
} from "./estado";

const TABLA = "tractores";

const enriquecer = (tr) => {
  const empresasMap = cacheStore.empresas?.map || {};
  const empresa = empresasMap[String(tr.empresa)];

  // Ver estado.js: el campo trae "1"/"true"/"0"/"false"/""/null, y el
  // chequeo anterior daba "Activo" para todo texto no vacío — incluido
  // "false". Para tractores además se usa `estado_calculado`, que distingue
  // los vendidos y reactiva a los que reportan kilómetros.
  const estado = textoDeEstadoTractor(tr);

  /* LA COLUMNA "SATELITAL" GUARDA UN CUIT, no un nombre — igual que `empresa`.
   * Nunca se resolvía, así que la columna mostraba un guion en las 399 filas
   * que SÍ tienen el dato: se leía como "no tiene satelital" cuando el
   * problema era que nadie lo traducía.
   *
   * Se resuelve contra el mismo catálogo de empresas y con el mismo formato,
   * CUIT incluido.
   *
   * LO QUE NO ESTÁ EN EL CATÁLOGO SE MUESTRA IGUAL, con el CUIT crudo.
   * Esconderlo haría ver a ese camión como si no tuviera satelital.
   *
   * SON DOS PROVEEDORES Y LOS DOS SON REALES:
   *
   *     CAR TRUCK           30717246086   226 tractores   es LYNX/Cartrack
   *     MICHELIN ARGENTINA  30525514885   173 tractores   sin acceso todavía
   *
   * El de Michelin parecía un error de carga —Michelin suena a cubiertas— y no
   * lo es: presta el servicio satelital de esos 173. Lo que falta es el acceso
   * a su API, no el dato.
   *
   * Y ESA ES LA INFORMACIÓN QUE ESTA COLUMNA APORTA, que ninguna otra tiene.
   * La telemetría que sí leemos —LYNX, My SCANIA, rFMS— sólo puede decir quién
   * REPORTA; de los 173 de Michelin no diría nada, y ese silencio se lee igual
   * que un camión sin equipo. El padrón es la única fuente que distingue "no
   * tiene satelital" de "tiene, y todavía no lo podemos ver".
   *
   * Por eso NO se rellena esta columna con lo que reporta la telemetría:
   * pisaría el único dato que cubre el hueco con uno que no lo cubre.
   */
  const satelital = empresasMap[String(tr.satelital)];
  const cuitSat = String(tr.satelital ?? "").trim();

  /* Estadía abierta en el taller (#114): la pastilla de estado lo muestra
   * como «Condicional». CALCULADO, nunca escrito en `estado` — escrito se
   * desincroniza el día que el taller lo libere y nadie toque el padrón. La
   * clave del taller para un tractor es el interno pelado. */
  const estadia = (cacheStore.enTaller?.data || []).find(
    (e) => String(e.interno) === String(tr.id),
  );

  return {
    ...tr,
    label: `${tr.id} (${tr.dominio})`.trim(),
    nombreEmpresa: etiquetaDeFicha(tr.empresa, empresa),
    nombreSatelital: satelital
      ? `${satelital.nombre} (${satelital.id})`
      : cuitSat,
    estadoReal: estado,
    /* Si ese estado lo DECLARÓ una persona o lo dedujo el sistema. Va al lado
     * de la pastilla: «Activo» por kilómetros reportados es un hecho de la
     * telemetría y «Chocado» cargado a mano es alguien que fue a mirar — y si
     * está mal, hay a quién preguntarle. */
    estadoDeclarado: origenDeEstado(tr) === "declarado",
    enTaller: Boolean(estadia),
    tallerMotivo: estadia?.motivo || null,
  };
};

// Realtime para listado
export const subscribeTractores = (callback) =>
  subscribeTable(TABLA, (rows) => callback(rows.map(enriquecer)));

/* Mismo problema que arriba: `x.estado` a secas daba verdadero para el
 * texto "false". Ahora se usa el mismo criterio que la etiqueta, para que
 * la lista y el contador no puedan discrepar. */
export const getTractoresActivos = (lista) => lista.filter(tractorEstaActivo);

// data viene con el id adentro: { id, ...campos }
export const createTractor = ({ id, ...data }) => createRow(TABLA, id, data);

export const updateTractor = (id, data) => updateRow(TABLA, id, data);

export const deleteTractor = (id) => deleteRow(TABLA, id);
